import { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import logoLogin from "@/assets/images/logo-login.png";
import { ExternalLinks } from "@/core/constants/external-links";
import { Paths } from "@/core/constants/paths";
import { getAuthToken, isTokenExpired, redirectToBffLogin } from "@/modules/auth";
import styles from "./login-screen.module.scss";

type LoginFeedback = {
  code: string;
  severity: "success" | "info" | "warn" | "error";
  summary: string;
  detail: string;
};

type LoginLocationState = {
  logoutMessage?: LoginFeedback;
};

const AUTH_FAILED_DETAIL = "Não foi possível autenticar o usuário. Tente novamente.";
const INACTIVE_USER_DETAIL =
  "Seu acesso ao Hub está inativo. Procure um administrador do Hub para reativá-lo.";

function feedbackFromSearch(search: string): LoginFeedback | null {
  const params = new URLSearchParams(search);
  const error = params.get("error");

  if (!error) {
    return null;
  }

  const message = params.get("message")?.trim();

  return {
    code: error,
    severity: "error",
    summary: "Erro",
    detail: message || (error === "inactive_user" ? INACTIVE_USER_DETAIL : AUTH_FAILED_DETAIL),
  };
}

export function LoginScreen() {
  const token = getAuthToken();
  const location = useLocation();
  const navigate = useNavigate();

  const toastRef = useRef<Toast>(null);
  const hasShownFeedback = useRef(false);

  useEffect(() => {
    if (hasShownFeedback.current) {
      return;
    }

    const state = location.state as LoginLocationState | null;
    const feedback = feedbackFromSearch(location.search) ?? state?.logoutMessage;

    if (!feedback) {
      return;
    }

    hasShownFeedback.current = true;

    toastRef.current?.show({
      severity: feedback.severity,
      summary: feedback.summary,
      detail: feedback.detail,
      life: feedback.severity === "error" ? 5000 : 3000,
      closable: true,
    });

    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  if (token && !isTokenExpired(token)) {
    return <Navigate to={Paths.HOME} replace />;
  }

  function handleMicrosoftLogin() {
    redirectToBffLogin();
  }

  function handleRequestAccess() {
    window.location.assign(ExternalLinks.ACCESS_REQUEST);
  }

  return (
    <main className={styles.page} data-testid="login-screen">
      <Toast ref={toastRef} position="top-right" />
      <div className={styles.overlay} aria-hidden />

      <section className={styles.card} aria-labelledby="login-title">
        <div className={styles.brand}>
          <img
            className={styles.brandLogo}
            src={logoLogin}
            alt="Hub de Obras"
            width={88}
            height={34}
          />
        </div>

        <div className={styles.header}>
          <h1 id="login-title" className={styles.title}>
            Acesse o Hub de Obras
          </h1>
          <p className={styles.subtitle}>
            Gerencie seu portfólio de obras de forma inteligente, segura e integrada.
          </p>
        </div>

        <div className={styles.actions}>
          <Button
            type="button"
            className={styles.primaryButton}
            label="Fazer login com Conta Microsoft"
            onClick={handleMicrosoftLogin}
            data-testid="login-microsoft-button"
          />
          <Button
            type="button"
            className={styles.secondaryButton}
            label="Solicitar Acesso"
            onClick={handleRequestAccess}
            data-testid="login-request-access-button"
          />
        </div>

        <footer className={styles.footer}>
          <p className={styles.copyright}>© 2026 OceanPact. Todos os direitos reservados.</p>
          <span className={styles.supportLink}>Precisa de ajuda? Contate o suporte</span>
        </footer>
      </section>
    </main>
  );
}
